import { useState, useEffect } from 'react'
import { 
  Shield, Check, X, Globe, Briefcase, Languages, Star, Upload, 
  User, Mail, Phone, MapPin, Award, BookOpen, FileText, AlertCircle,
  ArrowRight, ArrowLeft, Building2, Clock, UserCheck
} from 'lucide-react'
import { agentMarketplaceApi } from '../lib/api'
import { useToast } from '../lib/ToastContext'
import { supabase } from '../lib/supabase'
import { COUNTRIES } from '../lib/localization'
import { Button, Card, Input, Select, Textarea, Badge, Spinner } from '../components/ui'

const SPECIALTIES = [
  'Corporate Law',
  'Tax Compliance',
  'Employment Law',
  'Intellectual Property',
  'Data Privacy',
  'M&A',
  'Commercial Contracts',
  'Regulatory Compliance',
  'Immigration Law',
  'Real Estate Law',
  'Family Law',
  'Criminal Law',
]

const LANGUAGES = ['English', 'French', 'Portuguese', 'Arabic', 'Swahili', 'Yoruba', 'Hausa', 'Igbo', 'Spanish', 'German']

export default function AgentRegistration({ onComplete, onCancel }) {
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState(null)
  const [user, setUser] = useState(null)
  const [form, setForm] = useState({
    // Account
    email: '',
    password: '',
    confirmPassword: '',
    
    // Personal Info
    name: '',
    phone: '',
    country: 'Nigeria',
    countries: ['Nigeria'],
    languages: ['English'],
    bio: '',
    experience_years: 0,
    
    // Professional
    specialties: [],
    firm: '',
    bar_number: '',
    license_country: 'Nigeria',
    hourly_rate: '',
    currency: 'USD',
    
    // Verification
    verification_documents: [],
  })
  const { toast } = useToast()

  // Check if already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        setUser(user)
        setForm(prev => ({ ...prev, email: user.email || '' }))
      }
    }
    checkAuth()
  }, [])

  const handleSignUp = async (e) => {
    e.preventDefault()
    setAuthLoading(true)
    setAuthError(null)

    if (form.password !== form.confirmPassword) {
      setAuthError('Passwords do not match')
      setAuthLoading(false)
      return
    }

    if (form.password.length < 8) {
      setAuthError('Password must be at least 8 characters')
      setAuthLoading(false)
      return
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            role: 'agent',
            full_name: form.name,
          }
        }
      })

      if (error) throw error
      if (data?.user) {
        setUser(data.user)
        toast('Account created! Please complete your profile.', 'success')
        setStep(2)
      }
    } catch (err) {
      setAuthError(err.message || 'Sign up failed')
      toast('Sign up failed: ' + (err.message || 'Unknown error'), 'error')
    }
    setAuthLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      countries: form.countries,
      languages: form.languages,
      bio: form.bio,
      experience_years: parseInt(form.experience_years) || 0,
      specialties: form.specialties,
      firm: form.firm,
      hourly_rate: parseFloat(form.hourly_rate) || 50,
      currency: form.currency,
      bar_number: form.bar_number,
      license_country: form.license_country,
      verification_documents: {
        bar_number: form.bar_number,
        license_country: form.license_country,
        documents: form.verification_documents,
      },
    }

    const { error } = await agentMarketplaceApi.register(payload)
    setLoading(false)
    
    if (error) {
      toast('Registration failed: ' + error, 'error')
    } else {
      toast('Application submitted for verification!', 'success')
      setStep(4) // Success step
      onComplete?.()
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return <AccountStep 
          form={form} 
          setForm={setForm} 
          onSubmit={handleSignUp}
          loading={authLoading}
          error={authError}
          user={user}
        />
      case 2:
        return <ProfileStep 
          form={form} 
          setForm={setForm} 
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      case 3:
        return <VerificationStep 
          form={form} 
          setForm={setForm} 
          onSubmit={handleSubmit}
          loading={loading}
          onBack={() => setStep(2)}
        />
      case 4:
        return <SuccessStep onComplete={onComplete} />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-brand rounded-lg flex items-center justify-center">
            <Shield size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">Become a Bizos Agent</h1>
            <p className="text-sm text-muted">Join our global network of compliance experts</p>
          </div>
        </div>

        {/* Steps Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                s < step ? 'bg-emerald-500 text-white' :
                s === step ? 'bg-brand text-white' : 
                'bg-neutral-200 text-neutral-500'
              }`}>
                {s < step ? <Check size={16} /> : s}
              </div>
              <span className={`text-xs font-semibold ${
                s === step ? 'text-brand' : 
                s < step ? 'text-emerald-600' : 'text-muted'
              }`}>
                {s === 1 ? 'Account' : s === 2 ? 'Profile' : s === 3 ? 'Verify' : 'Done'}
              </span>
              {s < 4 && <div className={`flex-1 h-0.5 ${
                s < step ? 'bg-emerald-500' : 'bg-neutral-200'
              }`} />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="p-6 md:p-8">
          {renderStep()}
        </Card>
      </div>
    </div>
  )
}

// ============================================================
// STEP 1: ACCOUNT CREATION
// ============================================================

function AccountStep({ form, setForm, onSubmit, loading, error, user }) {
  if (user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <UserCheck size={20} className="text-emerald-600" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Already signed in</p>
            <p className="text-xs text-emerald-700">{user.email}</p>
          </div>
        </div>
        <Button variant="primary" onClick={() => {}} className="w-full">
          Continue to Profile →
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <h3 className="text-base font-semibold text-ink">Create Your Account</h3>
      <p className="text-sm text-muted">Sign up to start your journey as a Bizos agent.</p>

      <div>
        <label className="label-above">Email Address</label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="agent@yopmail.com"
          required
          icon={<Mail size={16} />}
        />
      </div>

      <div>
        <label className="label-above">Full Name</label>
        <Input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Tunde Bakare"
          required
          icon={<User size={16} />}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-above">Password</label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="••••••••"
            required
            minLength={8}
          />
          <p className="text-[10px] text-muted mt-1">Min 8 characters</p>
        </div>
        <div>
          <label className="label-above">Confirm Password</label>
          <Input
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            placeholder="••••••••"
            required
          />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="text-rose-600 shrink-0 mt-0.5" />
          <p className="text-xs text-rose-600">{error}</p>
        </div>
      )}

      <Button 
        type="submit" 
        variant="primary" 
        className="w-full"
        loading={loading}
        disabled={loading}
      >
        Create Account & Continue
      </Button>

      <p className="text-center text-xs text-muted">
        By signing up, you agree to our Terms of Service and Privacy Policy.
      </p>
    </form>
  )
}

// ============================================================
// STEP 2: PROFILE
// ============================================================

function ProfileStep({ form, setForm, onNext, onBack }) {
  const [validationError, setValidationError] = useState(null)

  const validate = () => {
    if (form.countries.length === 0) {
      setValidationError('Please select at least one country')
      return false
    }
    if (form.specialties.length === 0) {
      setValidationError('Please select at least one specialty')
      return false
    }
    if (!form.bio || form.bio.length < 20) {
      setValidationError('Please provide a bio (minimum 20 characters)')
      return false
    }
    setValidationError(null)
    return true
  }

  const handleNext = () => {
    if (validate()) {
      onNext()
    }
  }

  return (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-ink">Professional Profile</h3>
      <p className="text-sm text-muted">Tell companies about your expertise and experience.</p>

      <div>
        <label className="label-above flex items-center gap-2">
          <Globe size={14} /> Countries You Serve
        </label>
        <p className="text-xs text-muted mb-2">Select all countries where you can provide compliance services.</p>
        <div className="flex flex-wrap gap-2">
          {COUNTRIES.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => {
                const updated = form.countries.includes(country)
                  ? form.countries.filter(c => c !== country)
                  : [...form.countries, country]
                setForm({ ...form, countries: updated })
              }}
              className={`px-3 py-1.5 text-xs font-semibold border transition-colors rounded-full ${
                form.countries.includes(country)
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-neutral-200 text-neutral-500 hover:text-ink'
              }`}
            >
              {country}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label-above flex items-center gap-2">
          <Briefcase size={14} /> Specialties
        </label>
        <p className="text-xs text-muted mb-2">Select your areas of expertise.</p>
        <div className="flex flex-wrap gap-2">
          {SPECIALTIES.map((specialty) => (
            <button
              key={specialty}
              type="button"
              onClick={() => {
                const updated = form.specialties.includes(specialty)
                  ? form.specialties.filter(s => s !== specialty)
                  : [...form.specialties, specialty]
                setForm({ ...form, specialties: updated })
              }}
              className={`px-3 py-1.5 text-xs font-semibold border transition-colors rounded-full ${
                form.specialties.includes(specialty)
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-neutral-200 text-neutral-500 hover:text-ink'
              }`}
            >
              {specialty}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label-above flex items-center gap-2">
          <Languages size={14} /> Languages
        </label>
        <div className="flex flex-wrap gap-2">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                const updated = form.languages.includes(lang)
                  ? form.languages.filter(l => l !== lang)
                  : [...form.languages, lang]
                setForm({ ...form, languages: updated })
              }}
              className={`px-3 py-1.5 text-xs font-semibold border transition-colors rounded-full ${
                form.languages.includes(lang)
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-neutral-200 text-neutral-500 hover:text-ink'
              }`}
            >
              {lang}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label-above">Bio</label>
        <Textarea
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          placeholder="Describe your experience, expertise, and why companies should work with you..."
          rows={4}
          required
        />
        <p className="text-[10px] text-muted mt-1">
          {form.bio.length}/500 characters minimum 20
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-above">Years of Experience</label>
          <Input
            type="number"
            value={form.experience_years}
            onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
            placeholder="5"
            min="0"
          />
        </div>
        <div>
          <label className="label-above">Firm / Agency</label>
          <Input
            value={form.firm}
            onChange={(e) => setForm({ ...form, firm: e.target.value })}
            placeholder="Bakare & Associates"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-above">Hourly Rate</label>
          <Input
            type="number"
            value={form.hourly_rate}
            onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
            placeholder="50"
            min="0"
            step="5"
          />
        </div>
        <div>
          <label className="label-above">Currency</label>
          <Select
            value={form.currency}
            onChange={(value) => setForm({ ...form, currency: value })}
            options={[
              { value: 'USD', label: 'USD ($)' },
              { value: 'EUR', label: 'EUR (€)' },
              { value: 'GBP', label: 'GBP (£)' },
              { value: 'NGN', label: 'NGN (₦)' },
              { value: 'KES', label: 'KES (KSh)' },
            ]}
          />
        </div>
      </div>

      {validationError && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">{validationError}</p>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <Button variant="primary" onClick={handleNext} className="flex-1">
          Continue <ArrowRight size={14} className="ml-1" />
        </Button>
      </div>
    </div>
  )
}

// ============================================================
// STEP 3: VERIFICATION
// ============================================================

function VerificationStep({ form, setForm, onSubmit, loading, onBack }) {
  const [selectedFiles, setSelectedFiles] = useState([])

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    setSelectedFiles(files)
    setForm({ 
      ...form, 
      verification_documents: files.map(f => f.name) 
    })
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <h3 className="text-base font-semibold text-ink">Verification</h3>
      <p className="text-sm text-muted">Verify your credentials to build trust with companies.</p>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Why verification?</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Companies trust agents who are verified. Provide your bar number and license details 
              for platform verification. Verified agents appear higher in search results.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label-above flex items-center gap-2">
            <Award size={14} /> Bar / License Number
          </label>
          <Input
            value={form.bar_number}
            onChange={(e) => setForm({ ...form, bar_number: e.target.value })}
            placeholder="BAR-2024-001"
            required
          />
        </div>
        <div>
          <label className="label-above">License Country</label>
          <Select
            value={form.license_country}
            onChange={(value) => setForm({ ...form, license_country: value })}
            options={COUNTRIES.map(c => ({ value: c, label: c }))}
          />
        </div>
      </div>

      <div>
        <label className="label-above flex items-center gap-2">
          <FileText size={14} /> Verification Documents
        </label>
        <div className="border-2 border-dashed border-neutral-200 p-6 text-center rounded-lg hover:border-brand transition-colors">
          <Upload size={24} className="mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-muted">Upload your license certificate or bar registration</p>
          <p className="text-xs text-muted mt-1">PDF, PNG, or JPG (Max 5MB)</p>
          <input 
            type="file" 
            className="mt-3 block w-full text-sm text-muted file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-soft file:text-brand hover:file:bg-brand-soft/80"
            accept=".pdf,.png,.jpg,.jpeg"
            multiple
            onChange={handleFileChange}
          />
          {selectedFiles.length > 0 && (
            <div className="mt-3 text-left">
              <p className="text-xs font-semibold text-ink">Selected files:</p>
              {selectedFiles.map((f, i) => (
                <p key={i} className="text-xs text-muted">• {f.name}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button variant="secondary" onClick={onBack} className="flex-1">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <Button 
          type="submit" 
          variant="primary" 
          className="flex-1"
          loading={loading}
          disabled={loading || !form.bar_number}
        >
          {loading ? 'Submitting...' : 'Submit Application'}
        </Button>
      </div>
    </form>
  )
}

// ============================================================
// STEP 4: SUCCESS
// ============================================================

function SuccessStep({ onComplete }) {
  return (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
        <Check size={32} className="text-emerald-600" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-ink">Application Submitted!</h3>
        <p className="text-sm text-muted mt-2 max-w-md mx-auto">
          Your application is now being reviewed by the Bizos team. 
          You'll receive an email once your account is verified.
        </p>
      </div>
      <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg max-w-sm mx-auto">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-muted" />
          <span className="text-xs text-muted">Verification typically takes 1-2 business days</span>
        </div>
      </div>
      <Button variant="primary" onClick={onComplete}>
        Go to Dashboard
      </Button>
    </div>
  )
}